'use client'

import {
  DocumentIcon,
  DocumentPlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import {
  AnimatePresence,
  animate,
  motion,
  useIsPresent,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
} from 'framer-motion'
import {
  forwardRef,
  type DragEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'

export type QuizDocumentUploadStatus =
  | { type: 'uploaded' }
  | { type: 'uploading'; progress: number }

export type QuizDocumentUploadItem = {
  id: string
  name: string
  sizeLabel: string
  fileTypeLabel: string
  kind: 'image' | 'document'
  status: QuizDocumentUploadStatus
  note?: string
  thumbnailUrl?: string
}

export type QuizDocumentUploadProps = {
  documents: QuizDocumentUploadItem[]
  onAddDocument: () => void
  onNoteChange: (documentId: string, note: string) => void
  onRemoveDocument: (documentId: string) => void
  onDropDocuments?: (files: File[]) => void
  onDragActiveChange?: (active: boolean) => void
  dragActive?: boolean
  busy?: boolean
  acceptedFileDescription?: string
  className?: string
}

const calmControlTransition =
  'transition-[background-color,border-color,color,box-shadow,opacity] duration-[360ms] ease-[cubic-bezier(0.22,0.55,0.28,1)]'

export default function QuizDocumentUpload({
  documents,
  onAddDocument,
  onNoteChange,
  onRemoveDocument,
  onDropDocuments,
  onDragActiveChange,
  dragActive = false,
  busy = false,
  acceptedFileDescription = 'PDF, JPEG or PNG',
  className,
}: QuizDocumentUploadProps) {
  const prefersReducedMotion = useReducedMotion()
  const hasDocuments = documents.length > 0

  function handleDragEvent(event: DragEvent<HTMLElement>, nextActive: boolean) {
    if (!onDropDocuments) return

    event.preventDefault()
    event.stopPropagation()
    onDragActiveChange?.(nextActive)
  }

  function handleDrop(event: DragEvent<HTMLElement>) {
    if (!onDropDocuments) return

    event.preventDefault()
    event.stopPropagation()
    onDragActiveChange?.(false)

    const files = Array.from(event.dataTransfer.files)
    if (files.length > 0) onDropDocuments(files)
  }

  return (
    <motion.section
      animate={{ opacity: 1, y: 0 }}
      aria-label="Supporting documents"
      className={clsx('w-full font-sans text-[#0B0B0F]', className)}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
      layout="size"
      transition={{
        duration: prefersReducedMotion ? 0 : softMotionDuration.handoff,
        ease: softEditorialEase,
        layout: {
          duration: prefersReducedMotion ? 0 : softMotionDuration.handoff,
          ease: softEditorialEase,
        },
      }}
    >
      <div className="grid">
        <AnimatePresence initial={false} mode="sync">
          {hasDocuments ? (
            <UploadStatePanel
              key="documents"
              className="col-start-1 row-start-1"
              prefersReducedMotion={Boolean(prefersReducedMotion)}
            >
              <div className="flex flex-col gap-3 lg:gap-3.5">
                <AnimatePresence initial={true} mode="popLayout">
                  {documents.map((document, index) => (
                    <DocumentCard
                      document={document}
                      disabled={busy}
                      index={index}
                      key={document.id}
                      onNoteChange={onNoteChange}
                      onRemove={onRemoveDocument}
                      prefersReducedMotion={Boolean(prefersReducedMotion)}
                    />
                  ))}
                </AnimatePresence>

                <div className="flex h-[66px] flex-col items-center gap-2 pt-1 lg:h-24 lg:gap-2.5 lg:pb-[22px] lg:pt-1.5">
                  <AddDocumentButton
                    disabled={busy}
                    label="+ Add another"
                    onClick={onAddDocument}
                  />
                  <AcceptedFilesLabel>
                    {acceptedFileDescription}
                  </AcceptedFilesLabel>
                </div>
              </div>
            </UploadStatePanel>
          ) : (
            <UploadStatePanel
              key="empty"
              className="col-start-1 row-start-1"
              prefersReducedMotion={Boolean(prefersReducedMotion)}
            >
              <div
                aria-label="Document drop zone"
                className={clsx(
                  'flex h-[212px] w-full flex-col items-center justify-center gap-2.5 rounded-2xl border-[1.5px] border-dashed lg:h-[251px] lg:gap-3',
                  calmControlTransition,
                  dragActive
                    ? 'border-[#2563EB] bg-[#F7F9FF] shadow-[0_18px_44px_rgba(37,99,235,0.08)]'
                    : 'border-[#DADAE0] bg-white',
                )}
                onDragEnter={(event) => handleDragEvent(event, true)}
                onDragLeave={(event) => handleDragEvent(event, false)}
                onDragOver={(event) => handleDragEvent(event, true)}
                onDrop={handleDrop}
              >
                <DocumentPlusIcon
                  aria-hidden="true"
                  className="h-[26px] w-[26px] stroke-[1.7] text-[#A0A0A8] lg:h-[30px] lg:w-[30px]"
                />
                <p className="text-[14.5px] font-semibold leading-[19px] lg:text-[15px] lg:leading-5">
                  <span className="lg:hidden">Add a photo or document</span>
                  <span className="hidden lg:inline">
                    Drag a file here, or browse
                  </span>
                </p>
                <AddDocumentButton
                  disabled={busy}
                  label={
                    <>
                      <span className="lg:hidden">+ Browse files</span>
                      <span className="hidden lg:inline">+ Add a document</span>
                    </>
                  }
                  onClick={onAddDocument}
                />
                <AcceptedFilesLabel>
                  {acceptedFileDescription}
                </AcceptedFilesLabel>
              </div>
            </UploadStatePanel>
          )}
        </AnimatePresence>
      </div>
    </motion.section>
  )
}

type DocumentCardProps = {
  document: QuizDocumentUploadItem
  disabled: boolean
  index: number
  onNoteChange: (documentId: string, note: string) => void
  onRemove: (documentId: string) => void
  prefersReducedMotion: boolean
}

const DocumentCard = forwardRef<HTMLElement, DocumentCardProps>(
  function DocumentCard(
    { document, disabled, index, onNoteChange, onRemove, prefersReducedMotion },
    ref,
  ) {
    const status = document.status
    const isUploading = status.type === 'uploading'
    const progress =
      status.type === 'uploading'
        ? Math.round(Math.min(Math.max(status.progress, 0), 1) * 100)
        : 100

    return (
      <motion.article
        animate={{ opacity: 1, y: 0 }}
        className={clsx(
          'flex w-full flex-col gap-2.5 rounded-2xl border border-[#ECECEF] bg-white p-3.5 lg:gap-3 lg:px-[18px] lg:py-4',
          isUploading && 'min-h-[110px] lg:min-h-[124px]',
        )}
        exit={
          prefersReducedMotion
            ? { opacity: 0 }
            : {
                opacity: 0,
                y: -8,
                transition: {
                  duration: softMotionDuration.handoff,
                  ease: softEditorialEase,
                },
              }
        }
        initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
        layout
        ref={ref}
        transition={{
          delay: prefersReducedMotion ? 0 : Math.min(index, 2) * 0.07,
          duration: prefersReducedMotion ? 0 : softMotionDuration.handoff,
          ease: softEditorialEase,
          layout: {
            duration: prefersReducedMotion ? 0 : softMotionDuration.handoff,
            ease: softEditorialEase,
          },
        }}
      >
        <div className="flex min-w-0 items-center gap-3 lg:gap-3.5">
          <DocumentPreview document={document} />

          <div className="flex min-w-0 flex-1 flex-col gap-px lg:gap-0.5">
            <p className="truncate text-[14px] font-semibold leading-[18px] lg:text-[15px] lg:leading-5">
              {document.name}
            </p>
            <p className="truncate text-[12.5px] leading-4 text-[#82828B] lg:text-[13px] lg:leading-[17px]">
              {document.sizeLabel} · {document.fileTypeLabel}
              {!isUploading && ' — uploaded'}
            </p>
          </div>

          <button
            aria-label={`Remove ${document.name}`}
            className={clsx(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#A0A0A8]',
              calmControlTransition,
              'hover:bg-[#F4F4F6] hover:text-[#0B0B0F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB]',
            )}
            onClick={() => onRemove(document.id)}
            type="button"
          >
            {isUploading ? (
              <XMarkIcon
                aria-hidden="true"
                className="h-[13px] w-[13px] stroke-2 lg:h-3.5 lg:w-3.5"
              />
            ) : (
              <TrashIcon
                aria-hidden="true"
                className="h-[15px] w-[15px] stroke-2 lg:h-4 lg:w-4"
              />
            )}
          </button>
        </div>

        {!isUploading && (
          <DocumentNoteField
            disabled={disabled}
            note={document.note ?? ''}
            onChange={(note) => onNoteChange(document.id, note)}
          />
        )}

        {isUploading && (
          <UploadProgress
            documentName={document.name}
            prefersReducedMotion={prefersReducedMotion}
            progress={progress}
          />
        )}
      </motion.article>
    )
  },
)

function UploadProgress({
  documentName,
  prefersReducedMotion,
  progress,
}: {
  documentName: string
  prefersReducedMotion: boolean
  progress: number
}) {
  const animatedProgress = useMotionValue(0)
  const progressWidth = useTransform(animatedProgress, (latest) => `${latest}%`)
  const [displayedProgress, setDisplayedProgress] = useState(0)

  useMotionValueEvent(animatedProgress, 'change', (latest) => {
    setDisplayedProgress(Math.round(latest))
  })

  useEffect(() => {
    if (prefersReducedMotion) {
      animatedProgress.set(progress)
      setDisplayedProgress(progress)
      return
    }

    const controls = animate(animatedProgress, progress, {
      duration: softMotionDuration.handoff,
      ease: softEditorialEase,
    })

    return () => controls.stop()
  }, [animatedProgress, prefersReducedMotion, progress])

  return (
    <div className="flex flex-col gap-2.5 lg:gap-3">
      <div
        aria-label={`${documentName} upload progress`}
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={displayedProgress}
        className="h-[5px] overflow-hidden rounded-[3px] bg-[#ECECEF] lg:h-1.5"
        role="progressbar"
      >
        <motion.div
          className="h-full rounded-[3px] bg-[#0B0B0F]"
          style={{ width: progressWidth }}
        />
      </div>
      <p className="text-xs leading-[15px] text-[#82828B] lg:text-[12.5px] lg:leading-4">
        Uploading — {displayedProgress}%
      </p>
    </div>
  )
}

function DocumentNoteField({
  disabled,
  note,
  onChange,
}: {
  disabled: boolean
  note: string
  onChange: (note: string) => void
}) {
  const inputId = useId()
  const labelIsFloating = note.length > 0
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    const resizeToContent = () => {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.max(52, textarea.scrollHeight)}px`
    }

    resizeToContent()

    let previousWidth = textarea.offsetWidth
    const resizeObserver = new ResizeObserver(([entry]) => {
      const nextWidth = entry.contentRect.width
      if (nextWidth === previousWidth) return

      previousWidth = nextWidth
      resizeToContent()
    })

    resizeObserver.observe(textarea)
    return () => resizeObserver.disconnect()
  }, [note])

  return (
    <div className="relative">
      <textarea
        className={clsx(
          'peer block min-h-[52px] w-full resize-none overflow-hidden rounded-xl border border-transparent bg-[#F4F4F6] px-3 pb-2 pt-6 text-[13px] leading-[18px] text-[#0B0B0F] outline-none lg:px-3.5 lg:text-sm lg:leading-5',
          calmControlTransition,
          'focus:border-[#C8D4F0] focus:bg-white focus:shadow-[0_12px_30px_rgba(37,99,235,0.06)] disabled:cursor-wait disabled:opacity-60',
        )}
        disabled={disabled}
        id={inputId}
        onChange={(event) => onChange(event.target.value)}
        ref={textareaRef}
        rows={1}
        value={note}
      />
      <label
        className={clsx(
          'pointer-events-none absolute left-3 origin-left text-[13px] leading-[18px] text-[#82828B] transition-[top,transform,font-size,line-height,color] duration-[650ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none lg:left-3.5 lg:text-sm lg:leading-5',
          labelIsFloating
            ? 'top-1.5 translate-y-0 text-[11.5px] font-medium leading-[15px] lg:text-xs lg:leading-4'
            : 'top-1/2 -translate-y-1/2 peer-focus:top-1.5 peer-focus:translate-y-0 peer-focus:text-[11.5px] peer-focus:font-medium peer-focus:leading-[15px] lg:peer-focus:text-xs lg:peer-focus:leading-4',
        )}
        htmlFor={inputId}
      >
        Note for the doctor
      </label>
    </div>
  )
}

function UploadStatePanel({
  children,
  className,
  prefersReducedMotion,
}: {
  children: ReactNode
  className: string
  prefersReducedMotion: boolean
}) {
  const isPresent = useIsPresent()

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      aria-hidden={isPresent ? undefined : true}
      className={className}
      exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
      inert={isPresent ? undefined : true}
      transition={{
        duration: prefersReducedMotion ? 0 : softMotionDuration.handoff,
        ease: softEditorialEase,
      }}
    >
      {children}
    </motion.div>
  )
}

function DocumentPreview({ document }: { document: QuizDocumentUploadItem }) {
  if (document.kind === 'image') {
    return (
      <div
        aria-hidden="true"
        className="h-10 w-10 shrink-0 rounded-[10px] bg-cover bg-center lg:h-11 lg:w-11"
        style={{
          backgroundImage: document.thumbnailUrl
            ? `url(${JSON.stringify(document.thumbnailUrl)})`
            : 'linear-gradient(135deg, #F8DDCA 0%, #F3B475 100%)',
        }}
      />
    )
  }

  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#F4F4F6] lg:h-11 lg:w-11">
      <DocumentIcon
        aria-hidden="true"
        className="h-4 w-4 stroke-[1.8] text-[#A0A0A8] lg:h-[18px] lg:w-[18px]"
      />
    </div>
  )
}

function AddDocumentButton({
  disabled,
  label,
  onClick,
}: {
  disabled: boolean
  label: ReactNode
  onClick: () => void
}) {
  return (
    <button
      className={clsx(
        'flex min-h-[38px] items-center justify-center rounded-full border-[1.5px] border-[#0B0B0F]/20 px-[18px] text-[13.5px] font-semibold leading-[17px] lg:min-h-[41px] lg:px-5 lg:text-sm lg:leading-[18px]',
        calmControlTransition,
        'focus-visible:outline-offset-3 hover:border-[#0B0B0F]/45 hover:bg-[#F4F4F6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563EB] disabled:cursor-wait disabled:opacity-50',
      )}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {label}
    </button>
  )
}

function AcceptedFilesLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs leading-4 text-[#82828B] lg:text-[12.5px] lg:leading-[17px]">
      {children}
    </p>
  )
}
